import axios, { type InternalAxiosRequestConfig } from 'axios'
import axiosRetry, { isNetworkError } from 'axios-retry'
import * as Sentry from '@sentry/vue'
import { injectGAParams } from '@/utils/gaCookies'
import { useUserStore } from '@/stores/user'
import { useNotificationStore } from '@/stores/ui/notifications'
import {
  injectOrderToken,
  isOrderForbiddenError,
  ORDER_FORBIDDEN_MESSAGE,
  rememberOrderTokenFromResponse
} from '@/utils/orderAccessToken'
import type { App } from 'vue'

// DEVA-232: a 403 from an order route means the order secret is missing or wrong — tell the
// customer instead of leaving an empty page.
const notifyOrderForbidden = (error: unknown) => {
  if (isOrderForbiddenError(error)) {
    try {
      useNotificationStore().showError(ORDER_FORBIDDEN_MESSAGE)
    } catch {
      // store is unavailable (no active pinia) — the caller still gets the rejection
    }
  }
  return Promise.reject(error)
}

interface AxiosPluginOptions {
  baseUrl: string
}

export default {
  install: (app: App, options: AxiosPluginOptions) => {
    axios.defaults.headers.common = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-Project': import.meta.env.VITE_PROJECT_ALIAS
    }

    // Global request interceptor for axios
    // Injects client_id and session_id from Google Analytics cookies
    // for sending leads to Zoho CRM
    // Applies to all axios requests, including direct axios.post() calls
    axios.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        injectGAParams(config)
        injectOrderToken(config)
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    axios.interceptors.response.use(rememberOrderTokenFromResponse, notifyOrderForbidden)

    const axiosInstance = axios.create({
      baseURL: options.baseUrl
    })

    axiosRetry(axiosInstance, {
      retries: 3,
      retryDelay: (retryCount) => retryCount * 1000,
      retryCondition: (error) => {
        if (isNetworkError(error)) return true
        if (error.response && error.response.status >= 500) {
          return error.config?.method?.toLowerCase() === 'get'
        }
        return false
      }
    })

    // Request interceptor for the custom instance
    // Reuses the same GA injection logic via shared utility
    axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // The injected instance must send the logged-in user's Bearer token too (as useFetcher
        // does): POST /orders binds the order to the account only for an authenticated request,
        // otherwise the order never shows up in My Bookings.
        try {
          const userStore = useUserStore()
          if (userStore.isLoggedIn && userStore.token && !config.headers['Authorization']) {
            config.headers['Authorization'] = `Bearer ${userStore.token}`
          }
        } catch {
          // store is unavailable (no active pinia) — send the request as a guest
        }
        injectGAParams(config)
        injectOrderToken(config)
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    axiosInstance.interceptors.response.use(rememberOrderTokenFromResponse)

    axiosInstance.interceptors.response.use(null, (error) => {
      Sentry.captureException(error)
      return notifyOrderForbidden(error)
    })

    app.config.globalProperties.$axios = axiosInstance
    app.provide('axios', axiosInstance)
  }
}
